<!-- Copyright by the Spark Development Network; Licensed under the Rock Community License -->
<template>
    <fieldset>
        <div class="row">

            <div class="col-md-6">
                <TextBox v-model="name"
                         label="Name"
                         rules="required" />

                <EntityTypePicker v-if="showEntityTypePicker"
                                  v-model="entityType"
                                  label="Entity Type"
                                  rules="required"
                                  :multiple="false"
                                  :includeGlobalOption="false" />

                <div v-else class="form-group static-control ">
                    <label class="control-label">Entity Type</label>
                    <div class="control-wrapper">
                        <div class="form-control-static">
                            {{ entityType.text }}
                        </div>
                    </div>
                </div>

                <TextBox v-model="iconCssClass"
                         label="Icon CSS Class" />

                <ColorPicker v-model="color"
                             label="Color"
                             help="The base color to use for notes of this type. The background and foreground colors will be automatically calculated from this color." />

                <DropDownList v-if="showContentFormatDropdown"
                              v-model="formatType"
                              label="Content Format"
                              help="Structured format provides additional features and is the default for all new note types. Unstructured is a legacy format that is not checked for correctness and will be removed in the future."
                              :items="formatTypeItems"
                              :showBlankItem="true" />


                <div v-else class="form-group static-control ">
                    <label class="control-label">Content Format</label>
                    <div class="control-wrapper">
                        <div class="form-control-static">
                            Structured
                        </div>
                    </div>
                </div>

                <NotificationBox v-if="isChangingToStructuredFormat" alertType="warning">
                    Once you change a note type to the Structured format, it cannot be changed back. Be sure this is what you want to do.
                </NotificationBox>

                <template v-if="options.hasActiveAIProviders">
                    <div class="row">
                        <div class="col-md-6">
                            <CheckBox v-model="requiresApprovals"
                                      label="Enabled Approvals"
                                      @click="toggleRequiresApprovals()" />
                        </div>
                        <div class="col-md-6" v-if="requiresApprovals">
                            <CheckBox v-model="enabledAIApprovals"
                                      label="Enabled AI Approvals"
                                      help="Determines if AI should be used to automatically approve notes of this type. Ensure to provide guidelines on what constitutes an appropriate note." />

                        </div>
                    </div>

                    <TextBox
                             v-if="enabledAIApprovals"
                             v-model="aiApprovalGuidelines"
                             rules="required"
                             label="AI Approval Guidelines"
                             :help="aiApprovalGuidelineHelpText"
                             textMode="multiline" />

                    <AIProviderPicker
                                      v-if="enabledAIApprovals"
                                      v-model="aiProvider" label="AI Provider" help="The AI provider to use for the approval. If no provider is selected the default provider will be used." />
                </template>
            </div>

            <div class="col-md-6">

                <CheckBox v-model="userSelectable"
                          label="User Selectable" />

                <CheckBox v-model="allowsWatching"
                          label="Allows Watching"
                          help="If enabled, an option to watch individual notes will appear, and note watch notifications will be sent on watched notes." />

                <CheckBox v-model="autoWatchAuthors"
                          label="Auto Watch Authors"
                          help="If enabled, the author of a note will get notifications for direct replies to the note. In other words, a 'watch' will be automatically enabled on the note." />

                <CheckBox v-model="allowsReplies"
                          label="Allow Replies" />

                <TransitionVerticalCollapse>
                    <div v-if="allowsReplies">
                        <TextBox label="Max Reply Depth"
                                 class="input-width-sm"
                                 :rules="validateMaxReplyDepth"
                                 v-model="maxReplyDepth" />
                    </div>
                </TransitionVerticalCollapse>

                <CheckBox v-model="allowsAttachments"
                          label="Allows Attachments"
                          help="If enabled, then this note type will allow attachments. However, not all UI components will currently allow file uploads." />

                <TransitionVerticalCollapse>
                    <div v-if="allowsAttachments">
                        <BinaryFileTypePicker v-model="binaryFileType"
                                              label="Attachment File Type"
                                              help="When a file is attached to a note, it will be stored using this file type."
                                              rules="required" />
                    </div>
                </TransitionVerticalCollapse>

                <TransitionVerticalCollapse v-if="showEnableMentions">
                    <div v-if="showEnableMentions">
                        <CheckBox v-model="isMentionEnabled"
                                  label="Enable Mentions"
                                  help="Mentions allow a person to be mentioned in the text of a note. Once saved the mentioned person will be notified." />
                    </div>
                </TransitionVerticalCollapse>

            </div>

        </div>

        <AttributeValuesContainer v-model="attributeValues" :attributes="attributes" isEditMode :numberOfColumns="2" />
    </fieldset>
</template>

<script setup lang="ts">
    import { computed, PropType, ref, watch } from "vue";
    import AttributeValuesContainer from "@Obsidian/Controls/attributeValuesContainer.obs";
    import DropDownList from "@Obsidian/Controls/dropDownList.obs";
    import TextBox from "@Obsidian/Controls/textBox.obs";
    import EntityTypePicker from "@Obsidian/Controls/entityTypePicker.obs";
    import ColorPicker from "@Obsidian/Controls/colorPicker.obs";
    import CheckBox from "@Obsidian/Controls/checkBox.obs";
    import NotificationBox from "@Obsidian/Controls/notificationBox.obs";
    import BinaryFileTypePicker from "@Obsidian/Controls/binaryFileTypePicker.obs";
    import AIProviderPicker from "@Obsidian/Controls/aiProviderPicker.obs";
    import TransitionVerticalCollapse from "@Obsidian/Controls/transitionVerticalCollapse.obs";
    import { setPropertiesBoxValue, watchPropertyChanges } from "@Obsidian/Utility/block";
    import { propertyRef, updateRefValue } from "@Obsidian/Utility/component";
    import { NoteTypeBag } from "@Obsidian/ViewModels/Blocks/Core/NoteTypeDetail/noteTypeBag";
    import { NoteTypeDetailOptionsBag } from "@Obsidian/ViewModels/Blocks/Core/NoteTypeDetail/noteTypeDetailOptionsBag";
    import { NoteFormatType, NoteFormatTypeDescription } from "@Obsidian/Enums/Core/noteFormatType";
    import { enumToListItemBag } from "@Obsidian/Utility/enumUtils";
    import { ValidPropertiesBox } from "@Obsidian/ViewModels/Utility/validPropertiesBox";

    const props = defineProps({
        modelValue: {
            type: Object as PropType<ValidPropertiesBox<NoteTypeBag>>,
            required: true
        },

        options: {
            type: Object as PropType<NoteTypeDetailOptionsBag>,
            required: true
        }
    });

    const emit = defineEmits<{
        (e: "update:modelValue", value: ValidPropertiesBox<NoteTypeBag>): void;
        (e: "propertyChanged", value: string): void;
    }>();

    // #region Values

    const attributes = ref(props.modelValue.bag?.attributes ?? {});
    const attributeValues = ref(props.modelValue.bag?.attributeValues ?? {});
    const name = propertyRef(props.modelValue.bag?.name ?? "", "Name");
    const entityType = propertyRef(props.modelValue.bag?.entityType ?? {}, "EntityTypeId");
    const iconCssClass = propertyRef(props.modelValue.bag?.iconCssClass ?? "", "IconCssClass");
    const color = propertyRef(props.modelValue.bag?.color ?? "", "Color");
    const userSelectable = propertyRef(props.modelValue.bag?.userSelectable ?? false, "UserSelectable");
    const allowsWatching = propertyRef(props.modelValue.bag?.allowsWatching ?? false, "AllowsWatching");
    const autoWatchAuthors = propertyRef(props.modelValue.bag?.autoWatchAuthors ?? false, "AutoWatchAuthors");
    const allowsReplies = propertyRef(props.modelValue.bag?.allowsReplies ?? false, "AllowsReplies");
    const maxReplyDepth = propertyRef(props.modelValue.bag?.maxReplyDepth ?? "", "MaxReplyDepth");
    const allowsAttachments = propertyRef(props.modelValue.bag?.allowsAttachments ?? false, "AllowsAttachments");
    const binaryFileType = propertyRef(props.modelValue.bag?.binaryFileType ?? {}, "BinaryFileTypeId");
    const formatType = propertyRef(props.modelValue.bag?.formatType.toString() ?? "", "FormatType");
    const isMentionEnabled = propertyRef(props.modelValue.bag?.isMentionEnabled ?? false, "IsMentionEnabled");
    const requiresApprovals = propertyRef(props.modelValue.bag?.requiresApprovals ?? false, "RequiresApprovals");
    const enabledAIApprovals = propertyRef(props.modelValue.bag?.enabledAIApprovals ?? false, "AdditionalSettingsJson");
    const aiApprovalGuidelines = propertyRef(props.modelValue.bag?.aiApprovalGuidelines ?? "", "AdditionalSettingsJson");
    const aiProvider = propertyRef(props.modelValue.bag?.aiProvider ?? null, "AdditionalSettingsJson");
    const showEntityTypePicker = ref(props.modelValue.bag?.showEntityTypePicker);
    const showContentFormatDropdown = ref(props.modelValue.bag?.formatType !== NoteFormatType.Structured);

    const aiApprovalGuidelineHelpText = ref(`
    This field defines the criteria for AI to automatically approve notes. Consider including guidance on content appropriateness, where/how/who will be viewing the note, privacy considerations, etc. An example might be:
    <br /><br />
    This note represents a comment on a prayer request. Please approve it based on if it would be appropriate  to be displayed on a public website. Be sure that it does not have any personal information including full names of individuals, first names are ok.
    `);

    // The properties that are being edited. This should only contain
    // objects returned by propertyRef().
    const propRefs = [name, entityType, iconCssClass, color, userSelectable,
        allowsWatching, autoWatchAuthors, allowsReplies, maxReplyDepth, allowsAttachments,
        binaryFileType, formatType, isMentionEnabled, requiresApprovals, enabledAIApprovals, aiApprovalGuidelines, aiProvider];

    const formatTypeItems = enumToListItemBag(NoteFormatTypeDescription);

    // #endregion

    // #region Computed Values

    const isChangingToStructuredFormat = computed((): boolean => {
        return showContentFormatDropdown.value && formatType.value === NoteFormatType.Structured.toString();
    });

    /**
     * 04/04/2025 - NLB - This is a temporary fix to hide the mentions checkbox until we decide when members can access it.
     * Asana Ticket with more Details from Daniel H: https://app.asana.com/0/1208364266328691/1209885795399789/f
     */
    const showEnableMentions = computed((): boolean => {
        return formatType.value === NoteFormatType.Structured.toString();
    });

    function validateMaxReplyDepth(value: unknown): string | boolean {

        const stringValue = value as string;

        if (stringValue) {
            const num = Number(value);
            if (!isValidNumber(num)) {
                return "must be a valid number.";
            }
            else if (num < 0 || num > 9999) {
                return "must have a value between 0 and 9999.";
            }
        }

        return true;
    }

    /**
     * Function that's called after clicking the "Enabled Approvals" checkbox.
     * This function is called before the model value is changed so references
     * to requiresApprovals would be the previously selected value.
     */
    function toggleRequiresApprovals(): void {
        const wasRequired = requiresApprovals.value;
        if (wasRequired) {
            // If the "Enabled Approvals" checkbox was checked, but is no longer
            // then uncheck the "Enabled AI Approvals" checkbox as well.
            enabledAIApprovals.value = false;
        }
    }

    function isValidNumber(num: number): boolean {
        return !isNaN(num) && isFinite(num);
    }

    // #endregion

    // Watch for parental changes in our model value and update all our values.
    watch(() => props.modelValue, () => {
        updateRefValue(attributes, props.modelValue.bag?.attributes ?? {});
        updateRefValue(attributeValues, props.modelValue.bag?.attributeValues ?? {});
        updateRefValue(name, props.modelValue.bag?.name ?? "");
        updateRefValue(entityType, props.modelValue.bag?.entityType ?? {});
        updateRefValue(iconCssClass, props.modelValue.bag?.iconCssClass ?? "");
        updateRefValue(color, props.modelValue.bag?.color ?? "");
        updateRefValue(userSelectable, props.modelValue.bag?.userSelectable ?? false);
        updateRefValue(allowsWatching, props.modelValue.bag?.allowsWatching ?? false);
        updateRefValue(maxReplyDepth, props.modelValue.bag?.maxReplyDepth ?? "");
        updateRefValue(allowsAttachments, props.modelValue.bag?.allowsAttachments ?? false);
        updateRefValue(binaryFileType, props.modelValue.bag?.binaryFileType ?? {});
        updateRefValue(formatType, props.modelValue.bag?.formatType.toString() ?? "");
        updateRefValue(isMentionEnabled, props.modelValue.bag?.isMentionEnabled ?? false);
        updateRefValue(requiresApprovals, props.modelValue.bag?.requiresApprovals ?? false);
        updateRefValue(enabledAIApprovals, props.modelValue.bag?.enabledAIApprovals ?? false);
        updateRefValue(aiApprovalGuidelines, props.modelValue.bag?.aiApprovalGuidelines ?? "");
        updateRefValue(aiProvider, props.modelValue.bag?.aiProvider ?? null);
    });

    // Determines which values we want to track changes on (defined in the
    // array) and then emit a new object defined as newValue.
    watch([attributeValues, ...propRefs], () => {

        const newValue: ValidPropertiesBox<NoteTypeBag> = {
            bag: { ...props.modelValue.bag } as NoteTypeBag
        };

        setPropertiesBoxValue(newValue, "name", name.value);
        setPropertiesBoxValue(newValue, "entityType", entityType.value);
        setPropertiesBoxValue(newValue, "iconCssClass", iconCssClass.value);
        setPropertiesBoxValue(newValue, "color", color.value);
        setPropertiesBoxValue(newValue, "userSelectable", userSelectable.value ?? false);
        setPropertiesBoxValue(newValue, "allowsWatching", allowsWatching.value ?? false);
        setPropertiesBoxValue(newValue, "autoWatchAuthors", autoWatchAuthors.value ?? false);
        setPropertiesBoxValue(newValue, "allowsReplies", allowsReplies.value ?? false);
        setPropertiesBoxValue(newValue, "maxReplyDepth", maxReplyDepth.value);
        setPropertiesBoxValue(newValue, "allowsAttachments", allowsAttachments.value ?? false);
        setPropertiesBoxValue(newValue, "binaryFileType", binaryFileType.value);
        setPropertiesBoxValue(newValue, "formatType", parseInt(formatType.value ?? "") as NoteFormatType);
        setPropertiesBoxValue(newValue, "isMentionEnabled", isMentionEnabled.value ?? false);
        setPropertiesBoxValue(newValue, "requiresApprovals", requiresApprovals.value ?? false);
        setPropertiesBoxValue(newValue, "enabledAIApprovals", enabledAIApprovals.value ?? false);
        setPropertiesBoxValue(newValue, "aiApprovalGuidelines", aiApprovalGuidelines.value);
        setPropertiesBoxValue(newValue, "aiProvider", aiProvider.value ?? null);

        emit("update:modelValue", newValue);
    });

    // Watch for any changes to props that represent properties and then
    // automatically emit which property changed.
    watchPropertyChanges(propRefs, emit);
</script>
